import { StatusBadge } from './StatusBadge';
import { PlatformIcon } from './PlatformIcon';
import { PerformanceBadge } from './PerformanceBadge';
import { FunnelStageBadge, FunnelStage } from './FunnelStageBadge';
import HealthBadgeWithTooltip from './HealthBadgeWithTooltip';
import { formatCurrency, formatPercent, formatNumber, formatROAS } from '../lib/formatters';
import type { Campaign } from '../types/campaign';

interface Props {
  campaigns: Campaign[];
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (column: string) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (value: number) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onContextMenu?: (event: React.MouseEvent, campaign: Campaign) => void;
}

const columns = [
  { key: 'platform', label: 'Platform', sortable: true },
  { key: 'campaign_name', label: 'Campaign', sortable: true },
  { key: 'funnel_stage', label: 'Funnel', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'health_score', label: 'Health', sortable: true, align: 'center' as const },
  { key: 'perf', label: 'Perf', sortable: false, align: 'center' as const },
  { key: 'roas', label: 'ROAS', sortable: true, align: 'right' as const },
  { key: 'spend', label: 'Spend', sortable: true, align: 'right' as const },
  { key: 'impressions', label: 'Impressions', sortable: true, align: 'right' as const },
  { key: 'clicks', label: 'Clicks', sortable: true, align: 'right' as const },
  { key: 'ctr', label: 'CTR', sortable: true, align: 'right' as const },
  { key: 'cpc', label: 'CPC', sortable: true, align: 'right' as const },
  { key: 'conversions', label: 'Conv.', sortable: true, align: 'right' as const },
];

export function CampaignTable({
  campaigns,
  sortBy,
  sortDir,
  onSort,
  itemsPerPage,
  onItemsPerPageChange,
  currentPage,
  totalPages,
  onPageChange,
  onContextMenu,
}: Props) {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, campaigns.length);
  const currentData = campaigns.slice(startIndex, endIndex);

  return (
    <div className="overflow-hidden bg-white rounded-xl shadow dark:bg-gray-800">
      {/* Table Controls */}
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">Show</span>
          <select
            className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          >
            {[25, 50, 100].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500 dark:text-gray-400">entries</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {columns.map(({ key, label, sortable, align }) => (
                <th
                  key={key}
                  onClick={() => sortable && onSort(key)}
                  className={`px-4 py-3 text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 dark:text-gray-400 ${
                    sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''
                  } ${align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{label}</span>
                    {sortable && (
                      <div className="flex flex-col gap-0.5 ml-1">
                        <svg
                          className={`w-2 h-2 ${
                            sortBy === key && sortDir === 'asc'
                              ? 'text-blue-500'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 8 5"
                        >
                          <path d="M4.41 0.59L1.05 4.21C0.82 4.54 1.06 5 1.46 5H6.54C6.94 5 7.18 4.54 6.95 4.21L3.59 0.59C3.39 0.3 3.79 0.3 3.59 0.59Z" />
                        </svg>
                        <svg
                          className={`w-2 h-2 ${
                            sortBy === key && sortDir === 'desc'
                              ? 'text-blue-500'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 8 5"
                        >
                          <path d="M4.41 4.41L1.05 0.79C0.82 0.46 1.06 0 1.46 0H6.54C6.94 0 7.18 0.46 6.95 0.79L3.59 4.41C3.39 4.7 3.79 4.7 3.59 4.41Z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
            {currentData.map((campaign) => (
              <tr 
                key={campaign.unified_id} 
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                onContextMenu={(e) => {
                  if (onContextMenu) {
                    e.preventDefault();
                    onContextMenu(e, campaign);
                  }
                }}
                data-testid={`campaign-row-${campaign.unified_id}`}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <PlatformIcon platform={campaign.platform} />
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {campaign.campaign_name}
                  </div>
                  {campaign.objective && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {campaign.objective}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <FunnelStageBadge 
                    stage={campaign.funnel_stage as FunnelStage}
                    confidence={campaign.funnel_confidence}
                    campaignId={campaign.unified_id}
                    size="sm"
                    showConfidence={false}
                    editable={false}
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <StatusBadge status={campaign.status} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <HealthBadgeWithTooltip 
                    score={campaign.health_score ?? null}
                    breakdown={campaign.health_breakdown}
                    trend={campaign.health_trend}
                    trendChange={campaign.health_change_7d}
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <PerformanceBadge roas={campaign.roas} ctr={campaign.ctr} />
                </td>
                <td className="px-4 py-3 text-sm font-bold text-right whitespace-nowrap dark:text-white" 
                    style={{ color: campaign.roas && campaign.roas >= 3 ? '#10b981' : campaign.roas && campaign.roas >= 1.5 ? '#f59e0b' : '#ef4444' }}>
                  {formatROAS(campaign.roas)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 whitespace-nowrap dark:text-white">
                  {formatCurrency(campaign.spend)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 whitespace-nowrap dark:text-white">
                  {formatNumber(campaign.impressions)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 whitespace-nowrap dark:text-white">
                  {formatNumber(campaign.clicks)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 whitespace-nowrap dark:text-white">
                  {formatPercent(campaign.ctr)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 whitespace-nowrap dark:text-white">
                  {formatCurrency(campaign.cpc)}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-right text-gray-900 whitespace-nowrap dark:text-white">
                  {formatNumber(campaign.conversions)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {startIndex + 1} to {endIndex} of {campaigns.length} entries
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-900 dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-900 dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
